import React, { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import confirmAction from "../../utils/confirm";

const csrfToken = () => document.querySelector("[name=csrf-token]")?.content;

const TrixEditor = ({ content, onChange, inputId }) => {
  const editorRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const handleChange = () => {
      onChange(editor.innerHTML);
    };

    editor.addEventListener("trix-change", handleChange);

    // Load initial content once Trix is ready
    const handleInit = () => {
      if (!initializedRef.current && content) {
        editor.editor.loadHTML(content);
        initializedRef.current = true;
      }
    };

    if (editor.editor) {
      handleInit();
    } else {
      editor.addEventListener("trix-initialize", handleInit);
    }

    return () => {
      editor.removeEventListener("trix-change", handleChange);
      editor.removeEventListener("trix-initialize", handleInit);
    };
  }, []);

  return (
    <div className="trix-editor-container">
      <input type="hidden" id={inputId} />
      <trix-editor
        ref={editorRef}
        input={inputId}
        className="trix-content"
      />
    </div>
  );
};

const axiosConfig = () => ({
  headers: { "X-CSRF-TOKEN": csrfToken() },
});

const axiosMultipart = () => ({
  headers: { "X-CSRF-TOKEN": csrfToken(), "Content-Type": "multipart/form-data" },
});

const TabEditor = ({ tab, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast, locale }) => {
  const [label, setLabel] = useState(tab.label);
  const [content, setContent] = useState(tab.content);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState(tab.carousel_images || []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`/${locale}/admin/about_us/tabs/${tab.id}`, { label, content }, axiosConfig());
      onUpdate({ ...tab, label, content });
    } catch (e) {
      console.error("Failed to save tab:", e);
    }
    setSaving(false);
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    setUploading(true);
    const formData = new FormData();
    Array.from(files).forEach((f) => formData.append("images[]", f));
    try {
      const res = await axios.post(`/${locale}/admin/about_us/tabs/${tab.id}/carousel_images`, formData, axiosMultipart());
      setImages(res.data.carousel_images);
    } catch (e) {
      console.error("Failed to upload images:", e);
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleDeleteImage = (imageId) => {
    confirmAction(async () => {
      try {
        await axios.delete(`/${locale}/admin/about_us/tabs/${tab.id}/carousel_images/${imageId}`, axiosConfig());
        setImages(images.filter((img) => img.id !== imageId));
      } catch (e) {
        console.error("Failed to delete image:", e);
      }
    });
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">Tab: {tab.label}</h5>
          <div>
            <button className="btn btn-sm btn-outline-secondary mr-1" disabled={isFirst} onClick={onMoveUp}>&#9650;</button>
            <button className="btn btn-sm btn-outline-secondary mr-1" disabled={isLast} onClick={onMoveDown}>&#9660;</button>
            <button className="btn btn-sm btn-danger" onClick={() => confirmAction(() => onDelete(tab.id))}>Delete</button>
          </div>
        </div>

        <div className="form-group">
          <label>Label</label>
          <input type="text" className="form-control" value={label} onChange={(e) => setLabel(e.target.value)} />
        </div>

        <div className="form-group mt-2">
          <label>Content</label>
          <TrixEditor content={content} onChange={setContent} inputId={`tab-content-${tab.id}`} />
        </div>

        <button className="btn btn-primary mt-2" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Tab"}
        </button>

        <hr />
        <h6>Carousel Images</h6>
        <div className="row mb-2">
          {images.map((img) => (
            <div key={img.id} className="col-3 mb-2 position-relative">
              <img src={img.url} alt={img.filename} className="img-fluid rounded" style={{ maxHeight: 120 }} />
              <button
                className="btn btn-sm btn-danger position-absolute"
                style={{ top: 4, right: 20 }}
                onClick={() => handleDeleteImage(img.id)}
              >&times;</button>
            </div>
          ))}
        </div>
        <div className="form-group">
          <input type="file" className="form-control-file" accept="image/*" multiple onChange={handleImageUpload} disabled={uploading} />
          {uploading && <small className="text-muted">Uploading...</small>}
        </div>
      </div>
    </div>
  );
};

const AboutUsTabsManager = ({ initialTabs, locale }) => {
  const [tabs, setTabs] = useState(initialTabs || []);
  const [addingTab, setAddingTab] = useState(false);
  const [newTabLabel, setNewTabLabel] = useState("");

  const reorderTabs = useCallback(async (newTabs) => {
    setTabs(newTabs);
    const ids = newTabs.map((t) => t.id);
    try {
      await axios.patch(`/${locale}/admin/about_us/tabs/reorder`, { ids }, axiosConfig());
    } catch (e) {
      console.error("Failed to reorder tabs:", e);
    }
  }, [locale]);

  const moveItem = (list, index, direction) => {
    const newList = [...list];
    const swapIndex = index + direction;
    [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
    reorderTabs(newList);
  };

  const handleAddTab = async () => {
    if (!newTabLabel.trim()) return;
    setAddingTab(true);
    try {
      const res = await axios.post(`/${locale}/admin/about_us/tabs`, { label: newTabLabel }, axiosConfig());
      setTabs([...tabs, { ...res.data, content: "", carousel_images: [] }]);
      setNewTabLabel("");
    } catch (e) {
      console.error("Failed to create tab:", e);
    }
    setAddingTab(false);
  };

  const handleDeleteTab = async (id) => {
    try {
      await axios.delete(`/${locale}/admin/about_us/tabs/${id}`, axiosConfig());
      setTabs(tabs.filter((t) => t.id !== id));
    } catch (e) {
      console.error("Failed to delete tab:", e);
    }
  };

  const handleUpdateTab = (updatedTab) => {
    setTabs(tabs.map((t) => (t.id === updatedTab.id ? { ...t, ...updatedTab } : t)));
  };

  return (
    <div className="mt-3">
      {tabs.map((tab, index) => (
        <TabEditor
          key={tab.id}
          tab={tab}
          locale={locale}
          onUpdate={handleUpdateTab}
          onDelete={handleDeleteTab}
          onMoveUp={() => moveItem(tabs, index, -1)}
          onMoveDown={() => moveItem(tabs, index, 1)}
          isFirst={index === 0}
          isLast={index === tabs.length - 1}
        />
      ))}

      <div className="card mb-4">
        <div className="card-body">
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="New tab label (e.g. 2025-2030)"
              value={newTabLabel}
              onChange={(e) => setNewTabLabel(e.target.value)}
            />
            <div className="input-group-append">
              <button className="btn btn-success" onClick={handleAddTab} disabled={addingTab || !newTabLabel.trim()}>
                {addingTab ? "Adding..." : "Add Tab"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUsTabsManager;
